import React, { useEffect, useRef, useState } from 'react';
import { StickyNote } from 'lucide-react';

export const CLIENT_NOTE_MAX_LENGTH = 4000;

const HINT_STORAGE_KEY = 'tls-client.note-hint-focuses';
const HINT_FOCUS_LIMIT = 10;

interface ClientNoteEditorProps {
  value: string | null | undefined;
  onSave: (next: string) => Promise<void>;
  variant?: 'cell' | 'block';
  lang: 'ro' | 'en';
}

function readHintFocuses(): number {
  try {
    const n = Number(localStorage.getItem(HINT_STORAGE_KEY) || '0');
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function bumpHintFocuses(): number {
  const next = readHintFocuses() + 1;
  try {
    localStorage.setItem(HINT_STORAGE_KEY, String(next));
  } catch {
    /* private mode */
  }
  return next;
}

/**
 * Editor de notă personală (B/L sau container) pentru portalul claude-design.
 *
 * - `cell`: compact în tabel — click ca să editezi, salvare la blur.
 * - `block`: textarea vizibilă în modal, fără autofocus, salvare la blur.
 */
export const ClientNoteEditor: React.FC<ClientNoteEditorProps> = ({
  value,
  onSave,
  variant = 'block',
  lang,
}) => {
  const isRo = lang === 'ro';
  const isCell = variant === 'cell';
  const [draft, setDraft] = useState(value || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHint, setShowHint] = useState(() => readHintFocuses() < HINT_FOCUS_LIMIT);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const skipBlurPersist = useRef(false);
  const savedValue = value || '';

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (!isCell || !editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [editing, isCell]);

  const persist = async () => {
    if (draft.trim() === savedValue.trim()) {
      if (isCell) setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      if (isCell) setEditing(false);
    } catch {
      setDraft(savedValue);
    } finally {
      setSaving(false);
    }
  };

  const exitAndSave = async () => {
    skipBlurPersist.current = true;
    await persist();
    textareaRef.current?.blur();
    if (isCell) setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(savedValue);
      skipBlurPersist.current = true;
      textareaRef.current?.blur();
      if (isCell) setEditing(false);
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void exitAndSave();
    }
  };

  const onFocus = () => {
    if (!isCell) return;
    const n = bumpHintFocuses();
    if (n >= HINT_FOCUS_LIMIT) setShowHint(false);
  };

  const placeholder = isRo ? 'Adaugă o notă…' : 'Add a note…';
  const emptyLabel = isRo ? 'Adaugă notă' : 'Add note';

  const hintText = saving
    ? isRo
      ? 'Se salvează…'
      : 'Saving…'
    : isRo
      ? 'Se salvează automat la ieșirea din câmp'
      : 'Saves automatically when you leave the field';
  const showHintLine = saving || !isCell || showHint;

  const box = (
    <div className="client-note-box">
      <textarea
        ref={textareaRef}
        value={draft}
        maxLength={CLIENT_NOTE_MAX_LENGTH}
        placeholder={placeholder}
        disabled={saving}
        rows={isCell ? 3 : 4}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={onFocus}
        onBlur={() => {
          if (skipBlurPersist.current) {
            skipBlurPersist.current = false;
            return;
          }
          void persist();
        }}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      />
      {showHintLine && (
        <div className="client-note-hint">
          {hintText}
          {draft.length > 3500 ? ` · ${draft.length}/${CLIENT_NOTE_MAX_LENGTH}` : ''}
        </div>
      )}
    </div>
  );

  if (!isCell) {
    return (
      <div className="client-note-block">
        <div className="client-note-label">
          <StickyNote size={13} strokeWidth={2} />
          {isRo ? 'Notă' : 'Note'}
        </div>
        {box}
      </div>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={'client-note-trigger' + (savedValue ? '' : ' empty')}
        title={savedValue || emptyLabel}
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
      >
        <StickyNote size={13} strokeWidth={2} />
        {savedValue ? (
          <span className="client-note-trigger-text">{savedValue}</span>
        ) : (
          <span>{emptyLabel}</span>
        )}
      </button>
    );
  }

  return box;
};
